import {
  CheckForToken,
  OAuthURLResult,
  SheetsImportDeletionResult,
  SheetsImportInfoResult,
  SheetsImportPredictionsResult,
  SheetsImportTemplateStatus,
  PredictSheetsIOExport,
  PredictSheetsIOImportSheetCreateResult
} from '@futrli-sdk/predict';
import {
  API_PATHS,
  interpolatePathParams,
  REQUEST_METHODS,
  ROUTE_PARAMS
} from '@futrli/shared-services';
import { PeriodType, PERIOD_TYPE_REQUEST_DATA } from '../constants';
import { makeRequestWithResponse } from './shared';

/**
 * Checks whether an google sheets token exists
 *
 * @returns {Promise<CheckForToken>}
 * @throws error
 */
export const checkForToken = async (): Promise<CheckForToken> => {
  const path = API_PATHS.SHEETS_CHECK_FOR_TOKEN;
  return makeRequestWithResponse<CheckForToken>({ url: path, method: REQUEST_METHODS.GET });
};

/**
 * Returns a new oauth authorisation url for export
 *
 * @param {string} redirectURL
 * @returns {Promise<OAuthURLResult>}
 * @throws error
 */
export const fetchOauthURL = async (redirectURL: string): Promise<OAuthURLResult> => {
  const path = API_PATHS.SHEETS_AUTH_URL;
  return makeRequestWithResponse<OAuthURLResult>({
    url: path,
    method: REQUEST_METHODS.POST,
    data: {
      redirect_url: redirectURL
    }
  });
};

export interface ExcelExportResult {
  download_url: string;
}

/**
 * Returns a download url for an exported Excel file
 *
 * @param {string} workspaceId
 * @param {string} organisationId
 * @param {string} forecastId
 * @returns {Promise<ExcelExportResult>}
 * @throws error
 */
export const exportForecastToExcel = async (
  workspaceId: string,
  organisationId: string,
  forecastId: string
): Promise<ExcelExportResult> => {
  const path = interpolatePathParams(API_PATHS.FORECAST_EXCEL_EXPORT, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId
  });
  return makeRequestWithResponse<ExcelExportResult>({ url: path, method: REQUEST_METHODS.POST });
};

/**
 * Returns a url for an exported google sheet
 *
 * @param {string} workspaceId
 * @param {string} organisationId
 * @param {string} forecastId
 * @param {PeriodType} periodType
 * @param {boolean} isConsolidation
 * @returns {Promise<PredictSheetsIOExport>}
 * @throws error
 */
export const exportForecast = async (
  workspaceId: string,
  organisationId: string,
  forecastId: string,
  fileName: string,
  periodType?: PeriodType,
  isConsolidation = false
): Promise<PredictSheetsIOExport> => {
  const apiRoute = isConsolidation
    ? API_PATHS.CONSOLIDATED_FORECAST_EXPORT_GOOGLE_SHEETS
    : API_PATHS.SHEETS_EXPORT_FORECAST;

  const path = interpolatePathParams(apiRoute, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId
  });

  const params = periodType ? PERIOD_TYPE_REQUEST_DATA[periodType] : {};

  return makeRequestWithResponse<PredictSheetsIOExport>({
    url: path,
    method: REQUEST_METHODS.POST,
    params,
    data: { filename: fileName }
  });
};

/**
 * Creates a new master import google sheet
 *
 * @param {string} workspaceId
 * @param {string} organisationId
 * @param {string} forecastId
 * @returns {Promise<PredictSheetsIOImportSheetCreateResult>}
 * @throws error
 */
export const createImportSheet = async (
  workspaceId: string,
  organisationId: string,
  forecastId: string,
  fileName: string
): Promise<PredictSheetsIOImportSheetCreateResult> => {
  const path = interpolatePathParams(API_PATHS.SHEETS_IMPORT_CREATE, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId
  });

  return makeRequestWithResponse<PredictSheetsIOImportSheetCreateResult>({
    url: path,
    method: REQUEST_METHODS.POST,
    data: { filename: fileName }
  });
};

/**
 * Deletes data associated with the linked master sheet
 *
 * @param {string} workspaceId
 * @param {string} organisationId
 * @param {string} forecastId
 * @returns {Promise<SheetsImportDeletionResult>}
 * @throws error
 */
export const deleteImportSheet = async (
  workspaceId: string,
  organisationId: string,
  forecastId: string
): Promise<SheetsImportDeletionResult> => {
  const path = interpolatePathParams(API_PATHS.SHEETS_IMPORT_DELETE, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId
  });

  return makeRequestWithResponse<SheetsImportDeletionResult>({ url: path, method: REQUEST_METHODS.POST });
};

/**
 * Get info about a google sheets import master file
 *
 * @param {string} workspaceId
 * @param {string} organisationId
 * @param {string} forecastId
 * @returns {Promise<SheetsImportInfoResult>}
 * @throws error
 */
export const getImportSheetInfo = async (
  workspaceId: string,
  organisationId: string,
  forecastId: string
): Promise<SheetsImportInfoResult> => {
  const path = interpolatePathParams(API_PATHS.SHEETS_IMPORT_INFO, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId
  });

  return makeRequestWithResponse<SheetsImportInfoResult>({ url: path, method: REQUEST_METHODS.GET });
};

/**
 * Get status of available templates in import master file
 *
 * @param {string} workspaceId
 * @param {string} organisationId
 * @param {string} forecastId
 * @returns {Promise<SheetsImportTemplateStatus>}
 * @throws error
 */
export const getTemplateStatus = async (
  workspaceId: string,
  organisationId: string,
  forecastId: string
): Promise<SheetsImportTemplateStatus> => {
  const path = interpolatePathParams(API_PATHS.SHEETS_IMPORT_TEMPLATE_STATUS, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId
  });

  return makeRequestWithResponse<SheetsImportTemplateStatus>({ url: path, method: REQUEST_METHODS.GET });
};

/**
 * Import predictions from google sheets master file
 *
 * @param {string} workspaceId
 * @param {string} organisationId
 * @param {string} forecastId
 * @returns {Promise<SheetsImportPredictionsResult>}
 * @throws error
 */
export const importSheetPredictions = async (
  workspaceId: string,
  organisationId: string,
  forecastId: string
): Promise<SheetsImportPredictionsResult> => {
  const path = interpolatePathParams(API_PATHS.SHEETS_IMPORT_PREDICTIONS, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId
  });

  return makeRequestWithResponse<SheetsImportPredictionsResult>({ url: path, method: REQUEST_METHODS.POST });
};
